// src/components/Timer/MusicModal.js

import React, { useState, useEffect } from 'react';
import './MusicModal.css';

function MusicModal({ tracks, setTracks, onClose, defaultTracks }) {
  // Local state for tabs, search/filter, new track URL, etc.
  const [activeTab, setActiveTab] = useState('default'); // "default" or "custom"
  const [searchTerm, setSearchTerm] = useState('');
  const [newTrackUrl, setNewTrackUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Local state for fetched info for default tracks.
  // We store an object mapping track URLs to fetched info.
  const [defaultFetchedInfo, setDefaultFetchedInfo] = useState({});

  // Helper: Extract a YouTube video ID from a URL.
  const getYoutubeId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?\/)|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Helper: Fetch YouTube video info via the oEmbed API.
  const fetchYoutubeInfo = async (url) => {
    const endpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error('Failed to fetch video info');
    }
    return response.json();
  };

  // When the modal mounts, fetch updated info for default tracks.
  useEffect(() => {
    // Instead of iterating over defaultTracks prop, use the current tracks state
    // and fetch info for tracks that are not custom.
    tracks.forEach((track) => {
      if (!track.custom && getYoutubeId(track.url) && !defaultFetchedInfo[track.url]) {
        fetchYoutubeInfo(track.url)
          .then((info) => {
            setDefaultFetchedInfo((prev) => ({
              ...prev,
              [track.url]: info,
            }));
          })
          .catch((error) => {
            console.error('Error fetching default track info:', error);
          });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks]);

  // Add a new custom track using only a YouTube URL.
  const handleAddTrack = async () => {
    setErrorMessage('');
    if (!newTrackUrl.trim()) {
      setErrorMessage('Please provide a valid URL.');
      return;
    }
    try {
      setIsAdding(true);
      // Fetch video info to get the actual title and thumbnail.
      const info = await fetchYoutubeInfo(newTrackUrl);
      const newTrack = {
        name: info.title,         // Use the fetched YouTube title
        url: newTrackUrl,
        thumbnail: info.thumbnail_url,
        custom: true,             // Mark as custom
      };
      setTracks([...tracks, newTrack]);
      setNewTrackUrl('');
    } catch (err) {
      setErrorMessage('Unable to fetch YouTube video info. Please check the URL.');
    } finally {
      setIsAdding(false);
    }
  };

  // Delete a track (works for both default and custom tracks).
  const handleDeleteTrack = (trackToDelete) => {
    const updatedTracks = tracks.filter((track) => track !== trackToDelete);
    setTracks(updatedTracks);
  };

  // Reset the track list to only the default tracks.
  const handleResetToDefault = () => {
    setTracks([...defaultTracks]);
  };

  // Compute the displayed tracks from the overall tracks state.
  const displayedTracks =
    activeTab === 'default'
      ? tracks.filter((track) =>
          !track.custom &&
          track.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : tracks.filter(
          (track) =>
            track.custom &&
            track.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Manage Videos</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="tab-container">
          <div
            className={`tab ${activeTab === 'default' ? 'active' : ''}`}
            onClick={() => setActiveTab('default')}
          >
            Default Videos
          </div>
          <div
            className={`tab ${activeTab === 'custom' ? 'active' : ''}`}
            onClick={() => setActiveTab('custom')}
          >
            Your Videos
          </div>
        </div>

        {/* Search Input */}
        <input
          type="text"
          className="search-input"
          placeholder="Search Videos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Track List */}
        <div className="track-list">
          {displayedTracks.length > 0 ? (
            displayedTracks.map((track, index) => {
              // Determine a thumbnail.
              let thumb = track.thumbnail;
              if (!thumb) {
                // For default tracks, if info was fetched, use it.
                if (!track.custom && defaultFetchedInfo[track.url]?.thumbnail_url) {
                  thumb = defaultFetchedInfo[track.url].thumbnail_url;
                } else {
                  const id = getYoutubeId(track.url);
                  thumb = id
                    ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
                    : 'https://via.placeholder.com/200x112?text=No+Preview';
                }
              }
              // For the title, if this is a default track and fetched info exists, use the fetched title.
              let title = track.name;
              if (!track.custom && defaultFetchedInfo[track.url]?.title) {
                title = defaultFetchedInfo[track.url].title;
              }
              return (
                <div key={index} className="track-card">
                  <img src={thumb} alt={title} />
                  <div className="track-title">{title}</div>
                  <a
                    href={track.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="track-url"
                  >
                    {track.url}
                  </a>
                  <div className="track-actions">
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteTrack(track)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
              No videos found.
            </p>
          )}
        </div>

        {/* Add New Track Form (only for "Your Tracks" tab) */}
        {activeTab === 'custom' && (
          <div className="form-section">
            <h3>Add New Video</h3>
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            <div className="form-row">
              <input
                type="text"
                className="input-field"
                placeholder="Youtube URL"
                value={newTrackUrl}
                onChange={(e) => setNewTrackUrl(e.target.value)}
              />
            </div>
            <button
              className="add-button"
              onClick={handleAddTrack}
              disabled={isAdding}
            >
              {isAdding ? 'Adding...' : 'Add Video'}
            </button>
            <div className="modal-actions">
              <button className="reset-button" onClick={handleResetToDefault}>
                Reset to Default
              </button>
            </div>
          </div>
        )}

        {/* Footer with Close Button */}
        <div className="modal-actions">
          <button className="close-button-footer" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default MusicModal;
